import React, { useMemo } from 'react';
import { Table } from 'antd';
import { Colors } from 'works/legacy/lib/style-helper';
import { ImportShift } from '@scheduling/graphql';
import moment from 'moment';

type BadgeProp = { isUpdate: boolean };

function Badge({ isUpdate }: BadgeProp) {
  const backgroundColor = isUpdate
    ? Colors.harvestGoldHyperLight
    : Colors.skyLightest;

  return (
    <span
      style={{
        backgroundColor,
        borderRadius: '4px',
        padding: '4px',
      }}
    >
      {isUpdate ? 'Update' : 'New'}
    </span>
  );
}

function DiffShow({ diff }: { diff: number }) {
  const color =
    diff === 0
      ? Colors.textDefault
      : diff > 0
        ? Colors.skyRegular
        : Colors.rubyRed;
  const signal = diff > 0 ? '+' : '';

  return <span style={{ color, fontWeight: 'bolder' }}>{signal + diff}</span>;
}

const columns = [
  {
    key: 'unit',
    dataIndex: ['unit', 'name'],
    title: 'Unit Name',
  },
  {
    key: 'startDateTime',
    dataIndex: 'startDateTime',
    title: 'Start Date Time',
  },
  {
    key: 'endDateTime',
    dataIndex: 'endDateTime',
    title: 'End Date Time',
  },
  {
    key: 'role',
    dataIndex: ['role', 'abbreviation'],
    title: 'Role',
    className: 'members-add-multiple-review-table__role',
  },
  {
    key: 'isUpdate',
    dataIndex: 'isUpdate',
    title: 'Type',
    render: (value: boolean) => <Badge isUpdate={value} />,
  },
  {
    key: 'currentOpenings',
    dataIndex: 'currentOpenings',
    title: 'Current Openings',
    align: 'center' as const,
    render: (value: string) => <span>{value || 0}</span>,
  },
  {
    key: 'openingsDiff',
    dataIndex: 'openingsDiff',
    title: 'Openings +/-',
    align: 'center' as const,
    render: (value: string) => <DiffShow diff={parseInt(value)} />,
  },
];

type Props = {
  importShifts: ImportShift[];
};

export function ShiftsAddMultipleReviewTable({ importShifts }: Props) {
  const formatDateTime = (date: moment.MomentInput, time_zone: string) => {
    return moment(date).tz(time_zone).format('MM/DD/YYYY HH:mm');
  };

  const shifts = useMemo(() => {
    return importShifts.map((shift) => ({
      ...shift,
      startDateTime: formatDateTime(shift.startDateTime, shift.timeZone),
      endDateTime: formatDateTime(shift.endDateTime, shift.timeZone),
    }));
  }, [importShifts]);

  const pagination = {
    pageSize: 50,
  };

  return (
    <>
      <Table
        className="members-add-multiple-review-table"
        columns={columns}
        dataSource={shifts}
        pagination={pagination}
      />
      <style jsx>{`
        :global(.members-add-multiple-review-table .ant-table) {
          padding: 12px;
        }
        :global(.members-add-multiple-review-table .ant-table-tbody > tr > td) {
          padding: 12px 4px 12px 8px;
        }
        :global(.members-add-multiple-review-table__role) {
          text-transform: capitalize;
        }
      `}</style>
    </>
  );
}
